import { ROLE_DEFS, SCOPE_LABEL, ROLE_TONE_SELECTED_CLASSES } from '../roleTaxonomy';
import type { UserRoleId } from '../roleTaxonomy';

interface RoleCheckboxGroupProps {
  /** Currently-selected role ids (multi-select). */
  value: UserRoleId[];
  onChange: (ids: UserRoleId[]) => void;
  /** Roles the viewer may grant. Only these can be toggled; a selected role
   *  outside this set still shows, read-only. */
  grantableIds: UserRoleId[];
}

/** Multi-select role checkbox list for the invite drawer and edit modal. It
 *  offers only the roles the viewer may grant, plus any role already held
 *  that the viewer can't change. */
// eslint-disable-next-line max-lines-per-function -- 5-role checkbox list with inline svg + scope chip + disabled-reason hint; splitting scatters one tightly-coupled atom.
export default function RoleCheckboxGroup({
  value,
  onChange,
  grantableIds,
}: RoleCheckboxGroupProps) {
  function toggle(id: UserRoleId): void {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {ROLE_DEFS.filter((r) => grantableIds.includes(r.id) || value.includes(r.id)).map((r) => {
        const isDisabled = !grantableIds.includes(r.id);
        const isSelected = value.includes(r.id);
        const tone = ROLE_TONE_SELECTED_CLASSES[r.tone];
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => !isDisabled && toggle(r.id)}
            disabled={isDisabled}
            title={isDisabled ? 'Only an Admin can change this role.' : undefined}
            aria-pressed={isSelected}
            className={`flex items-start gap-2.5 text-left px-3 py-2.5 rounded-lg border transition ${
              isSelected && !isDisabled
                ? tone
                : 'bg-white dark:bg-[#1a1f1c] border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-[#242d28]'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`mt-0.5 w-4 h-4 rounded-[4px] border-[1.5px] flex items-center justify-center flex-none ${
                isSelected ? 'border-current bg-current' : 'border-stone-300 dark:border-stone-600'
              }`}
            >
              {isSelected && (
                /* The box is filled with `bg-current` (the tone colour), so
                   the tick colour has to contrast against the tone: white on
                   the deep light-mode tone, near-black on the light dark-mode
                   tone. */
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="text-white dark:text-[#141a16]"
                >
                  <path
                    d="M1.5 5L4 7.5L8.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[13px] font-bold text-bark-900 dark:text-[#e8ece9]">
                  {r.name}
                </span>
                <span className="text-[9.5px] font-bold tracking-wider uppercase text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-[#242d28] px-1.5 py-px rounded">
                  {SCOPE_LABEL[r.scope]}
                </span>
                {isDisabled && (
                  <span className="ml-auto text-[9.5px] font-bold uppercase text-clay-600">
                    Cannot change
                  </span>
                )}
              </div>
              <div className="text-[11px] text-stone-600 dark:text-stone-400 leading-snug">
                {r.desc}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
